import React, { useEffect, useMemo, useRef, useState } from 'react';
import './RecetaPreview.css';

const RecetaPreview = ({ consulta, onClose }) => {
  const watermarkLogo = '/recepie/logo-watermark.png';
  const clinicHeaderLogo = '/recepie/clinic-header-logo.png';
  const universityHeaderLogo = '';

  const especialidadesColumna1 = [
    'Pediatría',
    'Cirugía pediátrica',
    'Cirugía general',
    'Cirugía plástica',
    'Neurocirugía',
    'Cardiología',
    'Traumatología',
    'Medicina interna'
  ];

  const especialidadesColumna2 = [
    'Medicina general',
    'Psicología',
    'Medicina preventiva',
    'Urgencias',
    'Ultrasonido',
    'Laboratorio',
    'Farmacia'
  ];

  const normalizeDoctorName = (name) => {
    if (!name) return '';
    return name.replace(/^dr\.?\s+/i, '').trim();
  };

  const getProfessionalPrefix = (doctor) => {
    const explicitPrefix = doctor?.prefijo || doctor?.titulo_prefijo || doctor?.prefijo_profesional;
    if (explicitPrefix) return explicitPrefix;

    const roleSource = `${doctor?.tipo_profesional || ''} ${doctor?.profesion || ''} ${doctor?.titulo || ''}`.toLowerCase();
    if (roleSource.includes('quim')) return 'Q.F.B.';
    if (roleSource.includes('odont')) return 'OD.';
    if (roleSource.includes('medic')) return 'DR.';
    return 'DR.';
  };

  const formatDate = (date) => {
    if (!date) return new Date().toLocaleDateString('es-MX');
    return new Date(date).toLocaleDateString('es-MX');
  };

  const formatBirthDate = (date) => {
    if (!date) return '___/___/______';
    return new Date(date).toLocaleDateString('es-MX');
  };

  const calculateAge = (birthDate) => {
    if (!birthDate) return '___';
    const today = new Date();
    const birth = new Date(birthDate);
    let age = today.getFullYear() - birth.getFullYear();
    const monthDiff = today.getMonth() - birth.getMonth();
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birth.getDate())) age--;
    return age;
  };

  const normalizedDiagnostics = Array.isArray(consulta.diagnostico_cie10)
    ? consulta.diagnostico_cie10
        .map((diag) => {
          if (typeof diag === 'string') return { codigo: diag, descripcion: '' };
          return { codigo: diag?.codigo || '', descripcion: diag?.descripcion || '' };
        })
        .filter((diag) => diag.codigo)
    : [];

  const allDiagnosticos =
    normalizedDiagnostics.length > 0
      ? normalizedDiagnostics
          .map((diag) => `${diag.codigo}${diag.descripcion ? ` - ${diag.descripcion}` : ''}`)
          .join(' | ')
      : consulta.idx_diagnostico_presuntivo || '';

  const medicamentos = Array.isArray(consulta.medicamentos)
    ? consulta.medicamentos.filter((med) => med.nombre)
    : [];

  const doctorName = normalizeDoctorName(consulta.doctor?.nombre || '');
  const doctorPrefix = getProfessionalPrefix(consulta.doctor || {});
  const doctorUniversidad = consulta.doctor?.universidad || 'Universidad Nacional Autónoma de México';
  const doctorCedula = consulta.doctor?.cedula || '__________';
  const recetaFolio = consulta.folio || consulta.id || '';

  const clinicHeaderText1 = 'CLINICA DE ESPECIALIDADES GUARNEROS S.A. DE C.V.';
  const clinicHeaderText2 = 'Av. Ignacio Pichardo Pagaza, Esq. Valle de los Toltecas No. 70';
  const clinicHeaderText3 = 'Col. Fuentes de Aragón C.P. 55210 Ecatepec Estado de Mexico';
  const clinicHeaderText4 = `Tels. 55 57 75 32 28 - 55 57 74 90 88, ${doctorPrefix} ${doctorName}`;

  const verticalNoticeLine1 = 'ESTA CLÍNICA NO CUENTA';
  const verticalNoticeLine2a = 'CON SUCURSALES,';
  const verticalNoticeLine2b = 'NO SE DEJE ENGAÑAR.';
  const verticalNoticeLine3 = '"SOMOS ÚNICOS"';

  const warningLines = [
    '1.- SIGA LAS INSTRUCCIONES DE SU MÉDICO',
    '2.- EN CASO DE DUDA CONSULTAR A SU MÉDICO.',
    '3.- NO AUTORECETARSE, PUEDE OCASIONAR ALERGIAS Y RESISTENCIA AL ANTIBIÓTICOS.',
    '4.- NO INGERIR BEBIDAS ALCOHÓLICAS, PICANTE Y GRASAS.',
    '5.- NO DEJAR MEDICAMENTOS AL ALCANCE DE LOS NIÑOS.'
  ];

  const handlePrint = () => window.print();

  const initialCalibrationMode = useMemo(() => {
    if (typeof window === 'undefined') return false;
    const queryValue = new URLSearchParams(window.location.search).get('calibracion');
    if (queryValue === '1') return true;
    if (queryValue === '0') return false;
    return localStorage.getItem('receta_calibracion') === '1';
  }, []);

  const [calibrationMode, setCalibrationMode] = useState(initialCalibrationMode);
  const [calibrationMetrics, setCalibrationMetrics] = useState(null);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    localStorage.setItem('receta_calibracion', calibrationMode ? '1' : '0');
    const url = new URL(window.location.href);
    if (calibrationMode) {
      url.searchParams.set('calibracion', '1');
    } else {
      url.searchParams.delete('calibracion');
    }
    window.history.replaceState({}, '', url.toString());
  }, [calibrationMode]);

  const toggleCalibrationMode = () => {
    setCalibrationMode((value) => !value);
  };

  const copyCalibrationMetrics = async () => {
    if (!calibrationMetrics) return;
    const text = [
      `Domicilio -> línea siguiente: ${calibrationMetrics.gapDomicilio.toFixed(2)} mm`,
      `Cédula sup. -> Universidad: ${calibrationMetrics.gapCedulaUniversidad.toFixed(2)} mm`,
      `Recorte firma (+ cortado / - sobra): ${calibrationMetrics.signatureOverflow.toFixed(2)} mm`
    ].join('\n');

    try {
      await navigator.clipboard.writeText(text);
    } catch {
      console.log(text);
    }
  };

  const RecetaCopy = ({ copyLabel, reportMetrics, calibrationModeValue, consultaValue }) => {
    const copyInnerRef = useRef(null);
    const clinicTextsRef = useRef(null);
    const doctorLineRef = useRef(null);
    const domicilioRowRef = useRef(null);
    const nextRowRef = useRef(null);
    const signatureRef = useRef(null);

    const [metrics, setMetrics] = useState(null);

    useEffect(() => {
      if (!calibrationModeValue || !consultaValue) {
        setMetrics(null);
        return;
      }

      const calculateMetrics = () => {
        const copyInner = copyInnerRef.current;
        const clinicTexts = clinicTextsRef.current;
        const doctorLine = doctorLineRef.current;
        const domicilioRow = domicilioRowRef.current;
        const nextRow = nextRowRef.current;
        const signature = signatureRef.current;
        if (!copyInner || !domicilioRow || !nextRow || !signature) return;

        const copyRect = copyInner.getBoundingClientRect();
        const domicilioRect = domicilioRow.getBoundingClientRect();
        const nextRect = nextRow.getBoundingClientRect();
        const signatureRect = signature.getBoundingClientRect();
        const mmPerPx = 215 / copyRect.width;

        const gapDomicilio = (nextRect.top - domicilioRect.bottom) * mmPerPx;
        const signatureOverflow = (signatureRect.bottom - copyRect.bottom) * mmPerPx;

        let gapCedulaUniversidad = 0;
        if (clinicTexts && doctorLine) {
          const clinicRect = clinicTexts.getBoundingClientRect();
          const doctorRect = doctorLine.getBoundingClientRect();
          gapCedulaUniversidad = (doctorRect.top - clinicRect.bottom) * mmPerPx;
        }

        setMetrics({
          gapDomicilio,
          gapCedulaUniversidad,
          signatureOverflow
        });

        if (copyLabel === 'ORIGINAL' && reportMetrics) {
          reportMetrics({
            gapDomicilio,
            gapCedulaUniversidad,
            signatureOverflow
          });
        }
      };

      calculateMetrics();
      window.addEventListener('resize', calculateMetrics);
      return () => window.removeEventListener('resize', calculateMetrics);
    }, [calibrationModeValue, consultaValue, copyLabel, reportMetrics]);

    return (
    <div className={`receta-copy ${calibrationModeValue ? 'calibration-mode' : ''}`}>
      <div className="receta-copy-inner" ref={copyInnerRef}>
        <div className="receta-topbar">
          <div className="receta-header-left">
            {universityHeaderLogo ? (
              <img src={universityHeaderLogo} alt="Logo Universidad" className="receta-university-header-logo" />
            ) : (
              <div className="receta-university-header-placeholder" aria-hidden="true"></div>
            )}
          </div>

          <div className="receta-clinic">
            <div className="receta-clinic-texts" ref={clinicTextsRef}>
              <strong>{clinicHeaderText1}</strong>
              <small>{clinicHeaderText2}</small>
              <small>{clinicHeaderText3}</small>
              <small>{clinicHeaderText4}</small>
              <small>Cedula Profesional {doctorCedula}</small>
            </div>
          </div>

          <div className="receta-header-right">
            <img src={clinicHeaderLogo} alt="Logo Clínica" className="receta-clinic-header-logo" />
          </div>

          <div className="receta-doctor-line" ref={doctorLineRef}>
            <span className="receta-universidad">{doctorUniversidad}</span>
            <span className="receta-copy-label">{copyLabel}</span>
          </div>
        </div>

        <div className="receta-body-section">
        <div className="receta-patient-row">
          <div className="rx-field"><span className="rx-lbl">Nombre:</span> {consulta.nombre}</div>
          <div className="rx-field"><span className="rx-lbl">Edad:</span> {calculateAge(consulta.fecha_nacimiento)} años</div>
          <div className="rx-field"><span className="rx-lbl">F. Nac.:</span> {formatBirthDate(consulta.fecha_nacimiento)}</div>
          <div className="rx-field"><span className="rx-lbl">Fecha:</span> {formatDate(consulta.fecha_ingreso)}</div>
        </div>

        <div className="receta-dx-row" ref={domicilioRowRef}>
          <div className="rx-field rx-field-full">
            <span className="rx-lbl">Domicilio:</span> {consulta.receta_domicilio_controlado || '________________________________________'}
          </div>
        </div>

        <div className="receta-vitals-row" ref={nextRowRef}>
          <div className="rx-field rx-field-full"><span className="rx-lbl">DX:</span> {allDiagnosticos || '-'}</div>
          {consulta.tension_arterial_sistolica && (
            <div className="rx-field"><span className="rx-lbl">T/A:</span> {consulta.tension_arterial_sistolica}/{consulta.tension_arterial_diastolica}</div>
          )}
          {consulta.peso && <div className="rx-field"><span className="rx-lbl">Peso:</span> {consulta.peso}kg</div>}
          {consulta.temperatura && <div className="rx-field"><span className="rx-lbl">Temp:</span> {consulta.temperatura}°C</div>}
        </div>

        <div className="receta-rx-body">
          <div className="receta-watermark" aria-hidden="true">
            <div className="receta-watermark-col receta-watermark-col-left">
              {especialidadesColumna1.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
            <div className="receta-watermark-center">
              <img src={watermarkLogo} alt="" className="receta-watermark-logo" />
            </div>
            <div className="receta-watermark-col receta-watermark-col-right">
              {especialidadesColumna2.map((item) => (
                <span key={item}>{item}</span>
              ))}
            </div>
          </div>

          <div className="receta-vertical-notice">
            <span>{verticalNoticeLine1}</span>
            <span className="notice-quarter-break">{verticalNoticeLine2a}</span>
            <span>{verticalNoticeLine2b}</span>
            <span className="notice-underlined">{verticalNoticeLine3}</span>
          </div>

          <div className="rx-content">
            {medicamentos.length > 0 ? (
              <table className="rx-table">
                <thead>
                  <tr>
                    <th>Medicamento</th>
                    <th>Presentación</th>
                    <th>Dosis</th>
                    <th>Vía</th>
                    <th>Duración</th>
                    <th>Cant.</th>
                  </tr>
                </thead>
                <tbody>
                  {medicamentos.map((med, i) => (
                    <tr key={i}>
                      <td>{med.nombre}</td>
                      <td>{med.presentacion || '-'}</td>
                      <td>{med.dosis || '-'}</td>
                      <td>{med.via || '-'}</td>
                      <td>{med.duracion || '-'}</td>
                      <td>{med.cantidad || '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : consulta.tratamiento ? (
              <p className="rx-text">{consulta.tratamiento}</p>
            ) : (
              <p className="rx-text rx-empty">Sin prescripción</p>
            )}
          </div>

          <div className="receta-bottom-warnings">
            {warningLines.map((line) => (
              <div key={line}>{line}</div>
            ))}
          </div>
        </div>

        {consulta.receta_medicamento_controlado === 'Si' && (
          <div className="rx-controlled">
            Med. controlado {consulta.receta_domicilio_controlado ? `- Dom: ${consulta.receta_domicilio_controlado}` : ''}
          </div>
        )}
        </div>{/* end receta-body-section */}

        <div className="receta-bottom">
          <div className="receta-bottom-left">
            <div className="receta-footer-disclaimer">Este documento no es válido sin firma autógrafa</div>
            <div className="receta-footer-folio">Folio: {recetaFolio || '__________'}</div>
          </div>
          <div className="receta-signature" ref={signatureRef}>
            <div className="sig-line"></div>
            <span>{doctorPrefix} {doctorName}</span>
            <small>Céd. Prof. {doctorCedula}</small>
            <small>Fecha: {formatDate(consulta.fecha_ingreso)}</small>
          </div>
        </div>

        {calibrationModeValue && copyLabel === 'ORIGINAL' && metrics && <div className="calibration-panel-anchor" />}
      </div>
    </div>
  );
  };

  return (
    <div className="receta-preview-container">
      <div className="receta-actions no-print">
        <button onClick={onClose} className="btn btn-secondary">
          &#8592; Volver
        </button>
        <button onClick={toggleCalibrationMode} className="btn btn-secondary">
          {calibrationMode ? 'Calibración: ON' : 'Calibración: OFF'}
        </button>
        {calibrationMode && calibrationMetrics && (
          <div className="calibration-hud">
            <strong>Calibración (mm)</strong>
            <div>Domicilio → línea siguiente: {calibrationMetrics.gapDomicilio.toFixed(2)}</div>
            <div>Cédula sup. → Universidad: {calibrationMetrics.gapCedulaUniversidad.toFixed(2)}</div>
            <div>Recorte firma (+ cortado / - sobra): {calibrationMetrics.signatureOverflow.toFixed(2)}</div>
            <button type="button" className="btn btn-secondary calibration-copy-btn" onClick={copyCalibrationMetrics}>
              Copiar métricas
            </button>
          </div>
        )}
        <button onClick={handlePrint} className="btn btn-primary">
          Imprimir Receta
        </button>
      </div>

      <div className="receta-page">
        <RecetaCopy
          copyLabel="ORIGINAL"
          reportMetrics={setCalibrationMetrics}
          calibrationModeValue={calibrationMode}
          consultaValue={consulta}
        />
        <div className="receta-cut-line">
          <span>- - - - - - - - - - - - - - - - - - CORTAR AQUÍ - - - - - - - - - - - - - - - - - -</span>
        </div>
        <RecetaCopy
          copyLabel="COPIA"
          calibrationModeValue={calibrationMode}
          consultaValue={consulta}
        />
      </div>
    </div>
  );
};

export default RecetaPreview;
